import { useEffect, useRef, useState } from 'react';

// Column picker for the list tables (issue #148). Shows every column with a
// checkbox and, for the ones that can move, a drag handle plus move up and move
// down buttons.
//
// The move buttons are not a fallback. Native HTML5 drag cannot be started from
// the keyboard at all, so they are the accessible way to reorder; the drag is a
// mouse convenience on top.

const GripIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
    <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
    <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
  </svg>
);

const ColumnPicker = ({ columns, onToggle, onMove, onStep, onReset, isDefault }) => {
  const [open, setOpen] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const hiddenCount = columns.filter(c => !c.visible).length;
  const movable = columns.filter(c => !c.locked);

  const endDrag = () => { setDragKey(null); setOverKey(null); };

  const handleDrop = (targetKey) => {
    if (dragKey && dragKey !== targetKey) onMove(dragKey, targetKey);
    endDrag();
  };

  return (
    <div className="column-picker" ref={wrapRef}>
      <button
        type="button"
        className="btn-secondary"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }} aria-hidden="true">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" />
        </svg>
        Columns{hiddenCount > 0 ? ` (${columns.length - hiddenCount}/${columns.length})` : ''}
      </button>

      {open && (
        <div className="column-picker-panel" role="dialog" aria-label="Choose columns">
          <div className="column-picker-head">
            <span>Columns</span>
            <button
              type="button"
              className="btn-link"
              onClick={onReset}
              disabled={isDefault}
            >
              Reset to default
            </button>
          </div>

          <ul className="column-picker-list">
            {columns.map(col => {
              const position = movable.findIndex(c => c.key === col.key);
              return (
                <li
                  key={col.key}
                  className={[
                    'column-picker-item',
                    col.locked ? 'is-locked' : '',
                    dragKey === col.key ? 'is-dragging' : '',
                    overKey === col.key && dragKey && dragKey !== col.key ? 'is-drop-target' : '',
                  ].filter(Boolean).join(' ')}
                  draggable={!col.locked}
                  onDragStart={(e) => {
                    if (col.locked) return;
                    setDragKey(col.key);
                    // Safari will not start a drag unless data is set
                    e.dataTransfer.setData('text/plain', col.key);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragOver={(e) => {
                    if (col.locked || !dragKey) return;
                    e.preventDefault(); // without this the drop never fires
                    e.dataTransfer.dropEffect = 'move';
                    setOverKey(col.key);
                  }}
                  onDrop={(e) => { e.preventDefault(); if (!col.locked) handleDrop(col.key); }}
                  onDragEnd={endDrag}
                >
                  <span className="column-picker-grip" aria-hidden="true">
                    {col.locked ? null : <GripIcon />}
                  </span>

                  <label className="column-picker-label">
                    <input
                      type="checkbox"
                      checked={col.visible}
                      disabled={col.locked}
                      onChange={() => onToggle(col.key)}
                    />
                    <span>{col.label}</span>
                  </label>

                  {col.locked ? (
                    <span className="column-picker-note">Always shown</span>
                  ) : (
                    <span className="column-picker-steps">
                      <button
                        type="button"
                        onClick={() => onStep(col.key, -1)}
                        disabled={position <= 0}
                        aria-label={`Move ${col.label} left`}
                        title="Move left"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 15l-6-6-6 6" /></svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => onStep(col.key, 1)}
                        disabled={position === -1 || position >= movable.length - 1}
                        aria-label={`Move ${col.label} right`}
                        title="Move right"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6" /></svg>
                      </button>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ColumnPicker;
